import type * as THREE from "three";
import { Billboard, TreeType } from "./enums.js";

/** Values that may be `undefined` when a partially-populated (persisted) options object is copied
 *  in — the flat-scenario hazard documented in Set14 §4.2 c applies here too (never compare a
 *  possibly-absent numeric field with a bare `>`/`<`). */
export type Maybe<T> = T | undefined;

/** An index-keyed map of a per-branch-level option (e.g. `length[0]`, `angle[1]`). Keys are the
 *  branch recursion level they apply to; not every map contains every level. */
export type LevelMap = Record<number, Maybe<number>>;

/** A partial `TreeOptions` where every nested field is also optional — the shape preset JSONs (and
 *  partially-populated persisted options) actually provide, since they omit optional fields like
 *  `maps`/`map`/`roundedNormals` rather than explicitly setting them to their defaults. */
export type DeepPartial<T> = {
	[K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export interface TreeOptionsBark {
	/** Informational identifier carried through presets. The library does not consume this field;
	 *  the host app uses it to resolve which texture set to assign to `maps` below. */
	type: string;
	/** Texture maps supplied by the caller. Each entry is a THREE.Texture or null. When `textured`
	 *  is true, non-null maps are applied to the material; null maps fall back to the tint color
	 *  for that channel. */
	maps: {
		color: THREE.Texture | null;
		ao: THREE.Texture | null;
		normal: THREE.Texture | null;
		roughness: THREE.Texture | null;
	};
	/** Tint of the tree trunk */
	tint: number;
	/** Use face normals for shading instead of vertex normals */
	flatShading: boolean;
	/** Apply texture to bark */
	textured: boolean;
	/** Scale for the texture */
	textureScale: { x: number; y: number };
}

export interface TreeOptionsBranch {
	/** Number of branch recursion levels. 0 = trunk only */
	levels: Maybe<number>;
	/** Angle of the child branches relative to the parent branch (degrees) */
	angle: LevelMap;
	/** Number of children per branch level */
	children: LevelMap;
	/** External force encouraging tree growth in a particular direction */
	force: {
		direction: { x: number; y: number; z: number };
		strength: number;
	};
	/** Amount of curling/twisting at each branch level */
	gnarliness: LevelMap;
	/** Length of each branch level */
	length: LevelMap;
	/** Radius of each branch level */
	radius: LevelMap;
	/** Number of sections per branch level */
	sections: LevelMap;
	/** Number of radial segments per branch level */
	segments: LevelMap;
	/** Defines where child branches start forming on the parent branch */
	start: LevelMap;
	/** Taper at each branch level */
	taper: LevelMap;
	/** Amount of twist at each branch level */
	twist: LevelMap;
}

export interface TreeOptionsLeaves {
	/** Informational identifier (e.g. 'oak', 'ash'). Library does not consume it; the host app uses
	 *  it to resolve which texture to assign to `map`. */
	type: string;
	/** Color map supplied by the caller. THREE.Texture or null. When null, leaves render as a flat
	 *  tinted quad. */
	map: THREE.Texture | null;
	/** Whether to use single or double/perpendicular billboards */
	billboard: Billboard;
	/** Angle of leaves relative to parent branch (degrees) */
	angle: number;
	/** Number of leaves */
	count: number;
	/** Where leaves start to grow on the length of the branch (0 to 1) */
	start: number;
	/** Size of the leaves */
	size: number;
	/** Variance in leaf size between each instance */
	sizeVariance: number;
	/** Tint color for the leaves */
	tint: number;
	/** Controls transparency of leaf texture */
	alphaTest: number;
	/** Calculates custom normals to imply a rounded canopy shape */
	roundedNormals: boolean;
}

export interface TreeOptionsTrellis {
	/** Whether trellis is enabled */
	enabled: boolean;
	/** Position of trellis (z is distance from tree) */
	position: { x: number; y: number; z: number };
	/** Width of trellis grid (X direction) */
	width: number;
	/** Height of trellis grid (Y direction) */
	height: number;
	/** Distance between grid lines */
	spacing: number;
	/** Force parameters */
	force: {
		/** How strongly branches bend toward trellis */
		strength: number;
		/** Maximum distance at which trellis affects branches */
		maxDistance: number;
		/** Distance falloff exponent (1 = linear, 2 = quadratic) */
		falloff: number;
	};
	/** Radius of trellis cylinders */
	cylinderRadius: number;
	/** Whether to show trellis geometry */
	visible: boolean;
	/** Color of trellis */
	color: number;
}

export class TreeOptions {
	seed: number;
	type: TreeType;
	bark: TreeOptionsBark;
	branch: TreeOptionsBranch;
	leaves: TreeOptionsLeaves;
	trellis: TreeOptionsTrellis;

	constructor() {
		this.seed = 0;
		this.type = TreeType.Deciduous;

		// Bark parameters
		this.bark = {
			type: "Bark001",
			maps: {
				color: null,
				ao: null,
				normal: null,
				roughness: null,
			},
			tint: 0xffffff,
			flatShading: false,
			textured: true,
			textureScale: { x: 1, y: 1 },
		};

		// Branch parameters
		this.branch = {
			levels: 3,
			angle: {
				1: 70,
				2: 60,
				3: 60,
			},
			children: {
				0: 7,
				1: 7,
				2: 5,
			},
			force: {
				direction: { x: 0, y: 1, z: 0 },
				strength: 0.01,
			},
			gnarliness: {
				0: 0.15,
				1: 0.2,
				2: 0.3,
				3: 0.02,
			},
			length: {
				0: 20,
				1: 20,
				2: 10,
				3: 1,
			},
			radius: {
				0: 1.5,
				1: 0.7,
				2: 0.7,
				3: 0.7,
			},
			sections: {
				0: 12,
				1: 10,
				2: 8,
				3: 6,
			},
			segments: {
				0: 8,
				1: 6,
				2: 4,
				3: 3,
			},
			start: {
				1: 0.4,
				2: 0.3,
				3: 0.3,
			},
			taper: {
				0: 0.7,
				1: 0.7,
				2: 0.7,
				3: 0.7,
			},
			twist: {
				0: 0,
				1: 0,
				2: 0,
				3: 0,
			},
		};

		// Leaf parameters
		this.leaves = {
			type: "oak",
			map: null,
			billboard: Billboard.Double,
			angle: 10,
			count: 1,
			start: 0,
			size: 2.5,
			sizeVariance: 0.7,
			tint: 0xffffff,
			alphaTest: 0.5,
			roundedNormals: true,
		};

		// Trellis parameters
		this.trellis = {
			enabled: false,
			position: { x: 0, y: 0, z: -2 },
			width: 10,
			height: 20,
			spacing: 2,
			force: {
				strength: 0.02,
				maxDistance: 3,
				falloff: 1,
			},
			cylinderRadius: 0.05,
			visible: true,
			color: 0x8b4513,
		};
	}

	/**
	 * Copies the values from source into this object. The source may be a `DeepPartial` (e.g. a
	 * preset object or a partially-populated persisted options object) — only the keys it actually
	 * carries are copied, so `copy` is a merge-over-defaults, not a replace.
	 * @param source The options to copy from
	 * @param target The options to copy into (defaults to this)
	 */
	copy(source: DeepPartial<TreeOptions>, target: Partial<TreeOptions> = this) {
		for (const key in source) {
			if (source.hasOwnProperty(key) && target.hasOwnProperty(key)) {
				const value = (source as Record<string, unknown>)[key];
				// Assign THREE.Texture (and any non-plain object) by reference rather than recursing —
				// recursion would walk a Texture's internals.
				if (value !== null && typeof value === "object" && value.constructor === Object) {
					this.copy(value as DeepPartial<TreeOptions>, target[key] as Partial<TreeOptions>);
				} else {
					(target as Record<string, unknown>)[key] = value;
				}
			}
		}
	}
}

export default TreeOptions;
